import { Highlighter, LogIn, Users } from 'lucide-react';

import { LoginArea } from '@/components/auth/LoginArea';
import { HighlightCardSkeleton, HighlightList } from '@/components/HighlightList';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useHighlightsFeed, useParsedPages, type HighlightsFeedMode } from '@/hooks/useHighlights';

function EmptyState({ message }: { message: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 px-8 text-center">
        <Highlighter className="size-8 text-primary mx-auto mb-3" />
        <p className="text-muted-foreground max-w-sm mx-auto">{message}</p>
      </CardContent>
    </Card>
  );
}

function LoginPrompt({ mode }: { mode: HighlightsFeedMode }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 px-8 text-center space-y-4">
        <LogIn className="size-8 text-primary mx-auto" />
        <p className="text-muted-foreground max-w-sm mx-auto">
          {mode === 'mine'
            ? 'Log in to see your own highlights — your personal notebook of passages worth keeping.'
            : 'Log in to see highlights from people you follow.'}
        </p>
        <div className="flex justify-center">
          <LoginArea className="max-w-44" />
        </div>
      </CardContent>
    </Card>
  );
}

interface HighlightFeedProps {
  mode: HighlightsFeedMode;
}

/** Infinite-scrolling feed of highlight cards for the given feed mode. */
export function HighlightFeed({ mode }: HighlightFeedProps) {
  const { user } = useCurrentUser();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, contacts } =
    useHighlightsFeed(mode);

  const highlights = useParsedPages(data?.pages);

  if (mode !== 'global' && !user) {
    return <LoginPrompt mode={mode} />;
  }

  if (mode === 'following' && contacts.isError) {
    return (
      <EmptyState message="Couldn't load your contact list. Check your relay connections or try again in a moment." />
    );
  }

  if (mode === 'following' && contacts.isSuccess && contacts.data.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <Users className="size-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground max-w-sm mx-auto">
            You're not following anyone yet. Follow people on other Nostr clients and their
            highlights will show up here.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isPending) {
    return (
      <div className="space-y-4">
        <HighlightCardSkeleton />
        <HighlightCardSkeleton />
        <HighlightCardSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState message="Couldn't load highlights right now. Check your relay connections or try again in a moment." />
    );
  }

  if (highlights.length === 0) {
    return (
      <EmptyState
        message={
          mode === 'mine'
            ? "You haven't highlighted anything yet. Your marked passages will appear here."
            : mode === 'following'
              ? 'No highlights from people you follow yet. Try the global feed while you wait.'
              : 'No highlights found. Try checking your relay connections or wait a moment for content to load.'
        }
      />
    );
  }

  return (
    <HighlightList
      highlights={highlights}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
}
